import { ApolloError } from '@apollo/client';
import { OAuthError } from '@auth0/auth0-react';
import i18n from '@src/i18n';
import { logError } from './error-logger';

export function getApolloErrorCode(error: unknown): string | undefined {
  if (!(error instanceof ApolloError)) return undefined;
  const code = error.graphQLErrors[0]?.extensions?.code;
  return typeof code === 'string' ? code : undefined;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getFormattedErrorSuffix(error: unknown): string {
  return i18n.t('app.errors.suffix', 'Please try again later.');
}

export function formatError(
  error: unknown,
  context = i18n.t('app.errors.generic', 'Sorry, something went wrong.'),
): string {
  const suffix = getFormattedErrorSuffix(error);
  return `${context} ${suffix}`;
}

export function logAndFormatError(error: unknown, context?: string): string {
  logError(error);
  return formatError(error, context);
}

// OAuth error codes that originate from user actions (back button, stale tab,
// reused callback URL). Surfacing them to Sentry only adds noise.
const USER_DRIVEN_OAUTH_ERROR_CODES = new Set([
  'invalid_state',
  'invalid_grant',
]);

export function formatAndReportAuthError(error: unknown): string {
  if (
    error instanceof OAuthError &&
    error.error === 'access_denied' &&
    error.error_description?.includes('INVALID_ROLE')
  ) {
    return i18n.t(
      'auth.errors.access_denied',
      'You do not have permission to access this page.',
    );
  }
  if (
    !(error instanceof OAuthError) ||
    !USER_DRIVEN_OAUTH_ERROR_CODES.has(error.error)
  ) {
    logError(error);
  }
  return i18n.t(
    'auth.errors.generic',
    'Sorry, we could not log you in. Please try again.',
  );
}
